'use client';

import { motion, Variants } from 'framer-motion';
import { ChefHat, Utensils, LeafyGreen } from 'lucide-react';
import { InformativeTag } from './ui/custom/InformativeTag';

const OffersSectionClient = () => {
  const offers = [
    {
      title: 'Passion-Driven',
      description: `Jayouz isn't just a business; it's a passion project. We put love, tradition, and creativity into every meal we serve.`,
      icon: ChefHat,
      gradient: 'from-orange-500 via-red-500 to-pink-500',
      bg: 'from-orange-50 to-red-50',
    },
    {
      title: 'Fresh, Quality Ingredients',
      description:
        'We use fresh produce and ingredients, honoring traditional cooking methods while delivering the highest quality food.',
      icon: Utensils,
      gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
      bg: 'from-emerald-50 to-teal-50',
    },
    {
      title: 'Personalized Experience',
      description:
        'Every customer matters. We treat you like family, making sure your dining experience feels warm, welcoming, and tailored just for you.',
      icon: LeafyGreen,
      gradient: 'from-violet-500 via-purple-500 to-fuchsia-500',
      bg: 'from-violet-50 to-purple-50',
    },
  ];

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden">
      <div className="container-wrapper relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 space-y-8">
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>
              <InformativeTag>What make us better</InformativeTag>
            </motion.div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-800 mb-6 tracking-tight leading-tight">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                Jayouz
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto rounded-full mb-6"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
            Experience the perfect blend of traditional recipes and modern culinary innovation, handcrafted with love.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {offers.map((offer, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.03, y: -8 }}
              className="group relative">
              <div
                className={`bg-white/80 backdrop-blur-sm p-10 rounded-3xl shadow-lg group-hover:shadow-2xl transition-all duration-500 border border-white/50 relative overflow-hidden h-full`}>
                {/* Floating gradient orb */}
                <div
                  className={`absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br ${offer.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>

                <div
                  className={`w-20 h-20 bg-gradient-to-br ${offer.gradient} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg relative z-10`}>
                  <offer.icon className="w-10 h-10 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-orange-600 group-hover:to-amber-600 group-hover:bg-clip-text transition-all duration-300">
                  {offer.title}
                </h3>
                <p className="text-gray-600 text-lg font-light leading-relaxed">{offer.description}</p>

                {/* Decorative elements */}
                <motion.div
                  animate={{
                    rotate: [0, 360],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                  className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full opacity-20 blur-sm"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default OffersSectionClient;
